import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  RotateCcw,
  RotateCw,
  Save,
  Tag,
  QrCode,
  Loader2,
  Trash2,
  Download,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { cn } from "@/shared/lib/utils";
import { FirebaseImage } from "../components/FirebaseImage";

export type PhotoViewerPhoto = {
  url: string;
  /**
   * Stored 250 px thumbnail, used for the filmstrip only — the main image stays
   * full-resolution. Falls back to `url` when the photo has no thumbnail.
   */
  thumbnailUrl?: string;
  /** Stable id, when the photo has a database row. */
  id?: string;
  /** Highlight the nameplate button. */
  isNameplate?: boolean;
  /** Highlight the QR-code button. */
  isQrCode?: boolean;
  /**
   * False when the photo has no `asset_documents` row behind it — e.g. the entry
   * synthesised from an asset's primary-photo column. Tagging and deleting both
   * write to that row, so both are unavailable without it.
   */
  hasDocumentRow?: boolean;
};

export type PhotoViewerTag = "nameplate" | "qrCode";

type PhotoViewerModalProps = {
  photos: PhotoViewerPhoto[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Persist the previewed rotation. Receives the accumulated degrees and a
   * progress callback (0..100) for the upload. Omit for a read-only viewer —
   * the rotate and save controls then don't render.
   */
  onRotateSave?: (
    photo: PhotoViewerPhoto,
    degrees: number,
    onProgress: (percent: number) => void,
  ) => Promise<void>;
  /** Toggle a tag on the photo. Omit for a read-only viewer. */
  onSetTag?: (photo: PhotoViewerPhoto, tag: PhotoViewerTag) => Promise<void>;
  /** Move the photo to the recycle bin. Omit to hide the delete control. */
  onDelete?: (photo: PhotoViewerPhoto) => Promise<void>;
}

export function PhotoViewerModal({
  photos,
  initialIndex = 0,
  open,
  onOpenChange,
  onRotateSave,
  onSetTag,
  onDelete,
}: PhotoViewerModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [rotateDeg, setRotateDeg] = useState(0);
  const [saveProgress, setSaveProgress] = useState<number | null>(null);
  const [taggingTag, setTaggingTag] = useState<PhotoViewerTag | null>(null);
  // The main image is the full-resolution original (~2.4 MB), and a rotation
  // re-mints its URL into a fresh download. Without this the panel is just
  // empty for the duration.
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isSaving = saveProgress !== null;
  const canEdit = !!onRotateSave;

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      setRotateDeg(0);
    }
  }, [open, initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
        case "Escape":
          onOpenChange(false);
          break;
        case "+":
        case "=":
          handleZoomIn();
          break;
        case "-":
          handleZoomOut();
          break;
        case "0":
          handleResetView();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, currentIndex, photos.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
    resetView();
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
    resetView();
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.5, 4));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.5, 0.5));
  const handleResetView = () => resetView();

  /** Also discards an unsaved rotation, matching the forms dialog. */
  const resetView = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setRotateDeg(0);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDownload = async () => {
    const url = photos[currentIndex]?.url;
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `photo-${currentIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  if (!photos || photos.length === 0) return null;

  const photo = photos[currentIndex];
  if (!photo) return null;

  const handleSaveRotation = async () => {
    if (!onRotateSave || rotateDeg === 0 || isSaving) return;
    setSaveProgress(0);
    try {
      await onRotateSave(photo, rotateDeg, setSaveProgress);
      setRotateDeg(0);
    } catch {
      // The caller surfaces the failure. Keep the previewed rotation so the
      // user can retry instead of having to re-orient the photo from scratch.
    } finally {
      setSaveProgress(null);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || deleting) return;
    setDeleting(true);
    try {
      await onDelete(photo);
      setConfirmDelete(false);
      // The parent drops the photo from the list. Closing when it was the last
      // one avoids rendering against an empty array; otherwise step back so the
      // viewer doesn't land past the end.
      if (photos.length <= 1) onOpenChange(false);
      else setCurrentIndex((prev) => Math.min(prev, photos.length - 2));
    } catch {
      // Surfaced by the caller; keep the dialog open so it can be retried.
    } finally {
      setDeleting(false);
    }
  };

  const handleSetTag = async (tag: PhotoViewerTag) => {
    if (!onSetTag || taggingTag || photo.hasDocumentRow === false) return;
    setTaggingTag(tag);
    try {
      await onSetTag(photo, tag);
    } finally {
      setTaggingTag(null);
    }
  };

  const displayUrl = photo.url;
  const tagsDisabled = !onSetTag || photo.hasDocumentRow === false || !!taggingTag;
  const fullImageLoading = loadedUrl !== displayUrl;
  const placeholderUrl = photo.thumbnailUrl ?? null;
  // Rotation preview and zoom/pan must apply identically to the placeholder and
  // the full image, or they'd visibly jump apart as the full one arrives.
  const imageTransform = `rotate(${rotateDeg}deg) scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] p-0 bg-background/95 backdrop-blur-xl">
        <DialogHeader className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-background/90 to-transparent">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-sm font-medium">
              Photo {currentIndex + 1} of {photos.length}
              {zoom !== 1 && (
                <span className="ml-2 text-muted-foreground">
                  ({Math.round(zoom * 100)}%)
                </span>
              )}
              {rotateDeg !== 0 && (
                <span className="ml-2 text-primary">unsaved rotation</span>
              )}
            </DialogTitle>
            <div className="flex items-center gap-1">
              {canEdit && (
                <>
                  {isSaving ? (
                    <span className="flex items-center gap-1.5 px-2 text-xs text-primary">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {saveProgress}%
                    </span>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleSaveRotation}
                      disabled={rotateDeg === 0}
                      title="Save rotation"
                      aria-label="Save rotation"
                      className={rotateDeg !== 0 ? "text-primary" : undefined}
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleSetTag("nameplate")}
                    disabled={tagsDisabled}
                    title={
                      photo.hasDocumentRow === false
                        ? "This photo has no document record to tag"
                        : "Toggle nameplate"
                    }
                    aria-label="Toggle nameplate"
                    aria-pressed={!!photo.isNameplate}
                    className={photo.isNameplate ? "text-primary" : undefined}
                  >
                    <Tag className="h-4 w-4" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleSetTag("qrCode")}
                    disabled={tagsDisabled}
                    title={
                      photo.hasDocumentRow === false
                        ? "This photo has no document record to tag"
                        : "Toggle QR code"
                    }
                    aria-label="Toggle QR code"
                    aria-pressed={!!photo.isQrCode}
                    className={photo.isQrCode ? "text-primary" : undefined}
                  >
                    <QrCode className="h-4 w-4" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setRotateDeg((prev) => prev - 90)}
                    disabled={isSaving}
                    title="Rotate left"
                    aria-label="Rotate left"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setRotateDeg((prev) => prev + 90)}
                    disabled={isSaving}
                    title="Rotate right"
                    aria-label="Rotate right"
                  >
                    <RotateCw className="h-4 w-4" />
                  </Button>

                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setConfirmDelete(true)}
                      disabled={deleting || isSaving || photo.hasDocumentRow === false}
                      title={
                        photo.hasDocumentRow === false
                          ? "This photo has no document record to delete"
                          : "Delete photo"
                      }
                      aria-label="Delete photo"
                      className="text-destructive hover:text-destructive"
                    >
                      {deleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}

                  <div className="mx-1 h-5 w-px bg-border" />
                </>
              )}

              <Button variant="ghost" size="icon" onClick={handleZoomOut} title="Zoom out">
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleZoomIn} title="Zoom in">
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleResetView}
                title="Reset view"
                aria-label="Reset view"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleDownload} title="Download">
                <Download className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} title="Close">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Image Container */}
        <div
          className="relative flex-1 flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing h-full"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Instant low-res stand-in while the original downloads. */}
          {placeholderUrl && fullImageLoading && (
            <img
              src={placeholderUrl}
              alt=""
              aria-hidden
              className="absolute max-h-full max-w-full object-contain select-none blur-[2px]"
              style={{ transform: imageTransform }}
              draggable={false}
            />
          )}

          {fullImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary/70" />
            </div>
          )}

          <FirebaseImage
            src={displayUrl}
            alt={`Photo ${currentIndex + 1}`}
            onLoad={() => setLoadedUrl(displayUrl)}
            className={cn(
              "max-h-full max-w-full object-contain select-none transition-transform duration-200",
              fullImageLoading && "opacity-0",
            )}
            style={{ transform: imageTransform }}
            draggable={false}
          />

          {/* Navigation Arrows */}
          {photos.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-background/50 hover:bg-background/80"
                onClick={goToPrevious}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-background/50 hover:bg-background/80"
                onClick={goToNext}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}
        </div>

        {/* Thumbnail Strip */}
        {photos.length > 1 && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background/90 to-transparent">
            <div className="flex justify-center gap-2 overflow-x-auto py-2">
              {photos.map((p, index) => (
                <button
                  key={p.id ?? p.url}
                  onClick={() => {
                    setCurrentIndex(index);
                    resetView();
                  }}
                  className={cn(
                    "w-12 h-12 rounded-md overflow-hidden border-2 transition-all flex-shrink-0",
                    currentIndex === index
                      ? "border-primary ring-2 ring-primary/30"
                      : "border-transparent hover:border-primary/50"
                  )}
                >
                  <img
                    src={p.thumbnailUrl ?? p.url}
                    alt={`Thumbnail ${index + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
              <AlertDialogDescription>
                It moves to the recycle bin and can be restored from the assets
                editor. The image file itself is kept, because the mobile and web
                apps reference the same file.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  // Keep the alert open while the RPC runs so the spinner shows
                  // and a failure doesn't dismiss the confirmation.
                  e.preventDefault();
                  void handleDelete();
                }}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
